import { withRouter } from 'react-router';
import styled, { css } from 'styled-components';
import Configuration from 'scripts/constants/Configuration';
import { SubHeading } from 'src/design/styleguide/Headings';
import { boldFontWeight } from 'src/design/styleguide/common/fonts';
import {
  breakpointMedium,
  breakpointSmall,
} from 'src/design/styleguide/common/breakpoints';
import {
  advisorClientOnboardingUrl,
  advisorClientsUrl,
  advisorDashboardUrl,
  advisorPendingRequestsUrl,
  advisorSettingsUrl,
  advisorWalletUrl,
  boVedaFailedUrl,
  dashboardUrl,
  documentsUrl,
  logoutUrl,
  pendingOrdersUrl,
  portfolioUrl,
  settingsUrl,
  transactionsUrl,
  walletUrl,
} from 'src/utils/pageUrls';
import { getThemeColor } from 'src/themes/themeUtils';
import {
  gridGap,
  pageShellStyles,
} from 'src/components/dashboard/common/styles';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import { shadowDepth1 } from 'src/design/styleguide/common/embellishments';
import { testRefToDataTestReference } from 'src/design/styleguide/common/styledComponentsUtils';
import { connect } from 'react-redux';
import { userSelector } from 'scripts/redux/actions/user';
import ContentBlock from 'src/design/styleguide/contentBlock/ContentBlock';
import Expandable from 'src/design/components/expandable/Expandable';
import Hyperlink from 'src/design/components/hyperlink/Hyperlink';
import Icon from 'src/design/components/icon/Icon';
import Image from 'src/design/components/image/Image';
import RotatingIcon from 'src/design/components/icon/RotatingIcon';
import Spacing from 'src/design/styleguide/spacing/Spacing';
import Text from 'src/design/styleguide/text/Text';
import withFetching from 'src/decorators/withFetching';
import withState from 'src/decorators/withState';
import {
  hasMinorDetails,
  isMinorAccount,
  isSMSForTrustorCompanyAccount,
  isAdvisorAccount,
  isUserBoAllVerified,
} from 'scripts/utilities/userAccountHelper';
import {
  themeForBrickXMinorLandingPage,
  themeForIndividualAccount,
  themeForSmsfAccount,
  themeForAdvisorAccount,
} from 'src/themes/landingPageThemes';
import history from 'src/browser/history';

const investorMenuItems = [
  {
    href: dashboardUrl(),
    iconType: 'dashboard',
    text: 'Dashboard',
    itemTestRef: 'dashboard-home-nav-item',
  },
  {
    href: portfolioUrl(),
    iconType: 'portfolio',
    text: 'Portfolio',
    itemTestRef: 'portfolio-nav-item',
  },
  {
    href: walletUrl(),
    iconType: 'wallet',
    text: 'Wallet',
    itemTestRef: 'wallet-nav-item',
  },
  {
    href: pendingOrdersUrl(),
    iconType: 'pending_orders',
    text: 'Pending Orders',
    itemTestRef: 'pending-orders-nav-item',
  },
  {
    href: transactionsUrl(),
    iconType: 'transactions',
    text: 'Transaction History',
    itemTestRef: 'transaction-history-nav-item',
  },
  {
    href: documentsUrl(),
    iconType: 'documents',
    text: 'Documents',
    itemTestRef: 'documents-nav-item',
  },
  {
    href: settingsUrl(),
    iconType: 'settings',
    text: 'Account Settings',
    itemTestRef: 'settings-nav-item',
  },
  {
    href: logoutUrl(),
    iconType: 'logout',
    text: 'Logout',
    itemTestRef: 'logout-nav-item',
  },
];

const advisorMenuItems = [
  {
    href: advisorDashboardUrl(),
    iconType: 'dashboard',
    text: 'Dashboard',
    itemTestRef: 'advisor-dashboard-nav-item',
  },
  {
    href: advisorClientsUrl(),
    iconType: 'portfolio',
    text: 'Clients',
    itemTestRef: 'advisor-clients-nav-item',
  },
  {
    href: advisorWalletUrl(),
    iconType: 'wallet',
    text: 'Wallet',
    itemTestRef: 'advisor-wallet-nav-item',
  },
  {
    href: advisorPendingRequestsUrl(),
    iconType: 'pending_orders',
    text: 'Pending Requests',
    itemTestRef: 'advisor-pending-requests-nav-item',
  },
  {
    href: advisorClientOnboardingUrl(),
    iconType: 'documents',
    text: 'Client Onboarding',
    itemTestRef: 'advisor-client-onboarding-nav-item',
  },
  {
    href: advisorSettingsUrl(),
    iconType: 'settings',
    text: 'Account Settings',
    itemTestRef: 'advisor-settings-nav-item',
  },
  {
    href: logoutUrl(),
    iconType: 'logout',
    text: 'Logout',
    itemTestRef: 'advisor-logout-nav-item',
  },
];

const MenuHeaderContainer = styled.div.attrs(testRefToDataTestReference)`
  padding: ${gridUnit * 3}px ${gridUnit * 4}px ${gridUnit * 6}px
    ${gridUnit * 4}px;
  background: var(--dashboard-navigation-gradient);
  cursor: pointer;

  ${breakpointSmall(css`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${gridUnit * 12}px 0 ${gridUnit * 8}px 0;
    background: ${getThemeColor('surface')};
  `)}
`;

const CurrentPageHeading = styled(SubHeading)`
  display: flex;
  justify-content: space-between;

  ${breakpointSmall(`
    display: none;
  `)}
`;

const MenuHeaderIcon = styled(Image)`
  display: none;

  ${breakpointSmall(`
    display: block;
    width: 24px;
  `)}
`;

const MenuHeaderText = styled(Text).attrs({
  as: 'p',
  fontSize: { base: '3x-small', breakpointSmall: '4x-small' },
})`
  color: ${getThemeColor('onDarkSurfaceContrast')};

  ${breakpointSmall(css`
    color: ${getThemeColor('onSurfaceContrast800')};
  `)}
`;

const isActive = (location, href) => location.pathname.includes(href);

const withActiveRoute = (WrappedComponent) =>
  withRouter(({ location, ...props }) => (
    <WrappedComponent {...props} active={isActive(location, props.href)} />
  ));

const getMenuItems = (user) =>
  isAdvisorAccount(user) ? advisorMenuItems : investorMenuItems;

const getActiveMenuItemText = (location, user) => {
  const activeMenuItem = getMenuItems(user).find(({ href }) =>
    isActive(location, href),
  );
  return activeMenuItem ? activeMenuItem.text : '';
};

const renderCustomMenuHeaderIcon = (user) => {
  if (isMinorAccount(user)) {
    return (
      <MenuHeaderIcon src="/static/img/icon-dashboard-navigation-header-feature-yolk.svg" />
    );
  }
  if (isSMSForTrustorCompanyAccount(user)) {
    return (
      <MenuHeaderIcon src="/static/img/icon-dashboard-navigation-header-feature-blue.svg" />
    );
  }
  if (isAdvisorAccount(user)) {
    return (
      <MenuHeaderIcon src="/static/img/icon-dashboard-navigation-header-feature-purple.svg" />
    );
  }
  return (
    <MenuHeaderIcon src="/static/img/icon-dashboard-navigation-header-feature.svg" />
  );
};

const getAccountThemeColors = (user) => {
  if (isMinorAccount(user)) {
    return themeForBrickXMinorLandingPage.colors;
  }
  if (isSMSForTrustorCompanyAccount(user)) {
    return themeForSmsfAccount.colors;
  }
  if (isAdvisorAccount(user)) {
    return themeForAdvisorAccount.colors;
  }
  return themeForIndividualAccount.colors;
};

const getCustomThemeColor = (user) => getAccountThemeColors(user).primary;

const getAccountThemeGradient = (user) => {
  if (isMinorAccount(user)) {
    return 'var(--bx-color---gradient-yolk)';
  }
  if (isSMSForTrustorCompanyAccount(user)) {
    return 'var(--bx-color---gradient-blue)';
  }
  if (isAdvisorAccount(user)) {
    return 'var(--bx-color---gradient-purple)';
  }
  return 'var(--bx-color---gradient)';
};

const MenuHeader = withRouter(
  withFetching(
    [userSelector],
    [],
  )(({ user, location, expanded, ...props }) => {
    const hasMinorDetailsResult = hasMinorDetails(user);
    return (
      <MenuHeaderContainer {...props}>
        {renderCustomMenuHeaderIcon(user)}
        <Spacing
          top={{ base: 'none', breakpointSmall: 'normal' }}
          bottom={{ base: 'large', breakpointSmall: 'none' }}
        >
          <MenuHeaderText testRef="dashboard-navigation-greeting">
            {hasMinorDetailsResult ? (
              <span>
                {user.givenName}'s Account
                <br />
                <small>as trustee for {user.minorDetails.firstName}</small>
              </span>
            ) : (
              `${user.givenName}'s Account`
            )}
          </MenuHeaderText>
        </Spacing>
        <CurrentPageHeading
          fontSize="x-small"
          fontWeight="bold"
          color="onDarkSurfaceContrast"
          align="left"
          testRef="header-current-page"
        >
          {getActiveMenuItemText(location, user)}
          <RotatingIcon
            type="chevron_down"
            rotated={expanded}
            data-test-reference="chevron-icon"
          />
        </CurrentPageHeading>
      </MenuHeaderContainer>
    );
  }),
);

const NavigationPanel = styled.div`
  background: var(--dashboard-navigation-gradient);
  border-radius: var(--bx-radius);
  box-shadow: var(--bx-shadow);
  overflow: hidden;
  margin-bottom: var(--bx-ms---layout-mobile-padding);

  ${breakpointSmall(css`
    margin-left: 0;
    margin-right: 0;
    background: ${getThemeColor('surface')};
    padding-bottom: ${gridUnit * 22}px;
  `)}
`;

const MenuItemListPanel = styled(Expandable)`
  background: ${getThemeColor('surface')};
  box-shadow: ${shadowDepth1};

  ${breakpointSmall(`
    height: auto !important;
    box-shadow: none;
  `)}
`;

const MenuItemList = styled.ul`
  padding: ${gridUnit * 6}px 0;
  margin: 0;

  ${breakpointSmall(`
    padding: 0;
  `)}
`;

const MenuItemLink = styled(Hyperlink)`
  display: flex;
  align-items: center;
  padding: ${gridUnit * 4}px;
  color: ${getThemeColor('onSurfaceContrast500')};

  &:hover {
    background-color: var(--bx-color---background);
  }

  ${(props) =>
    props.active &&
    css`
      color: ${getThemeColor('onSurfaceContrast800')};
      border-left: ${gridUnit}px solid
        ${(p) => p.highlightColor || getThemeColor('primary')};
      padding-left: ${gridUnit * 3}px;
      font-weight: ${boldFontWeight};
      background-color: var(--bx-color---background);
    `}
`;

const MenuItemLi = styled.li`
  & + & {
    margin-top: ${gridUnit * 2}px;
  }
`;

const MenuItem = withActiveRoute(
  ({ className, text, iconType, highlightColor, ...props }) => (
    <MenuItemLi>
      <MenuItemLink
        className={className}
        highlightColor={highlightColor}
        {...props}
      >
        <Spacing right="normal">
          <Text as={Icon} type={iconType} fontSize="2x-small" />
        </Spacing>
        <Text as="p" fontSize="2x-small">
          {text}
        </Text>
      </MenuItemLink>
    </MenuItemLi>
  ),
);

const Navigation = ({
  className,
  expanded,
  onExpand,
  onItemSelect,
  user,
  testRef,
}) => {
  const navigationStyle = {
    '--dashboard-navigation-gradient': getAccountThemeGradient(user),
  };

  return (
    <div className={className} data-test-reference={testRef}>
      <NavigationPanel style={navigationStyle}>
        <MenuHeader
          expanded={expanded}
          onClick={onExpand}
          testRef="expandable-menu-header"
          style={navigationStyle}
        />
        <MenuItemListPanel expanded={expanded}>
          <MenuItemList>
            {getMenuItems(user).map(({ href, iconType, text, itemTestRef }) => (
              <MenuItem
                key={href}
                href={href}
                onClick={onItemSelect}
                iconType={iconType}
                text={text}
                highlightColor={getCustomThemeColor(user)}
                testRef={itemTestRef}
              />
            ))}
          </MenuItemList>
        </MenuItemListPanel>
      </NavigationPanel>
    </div>
  );
};

const PageContainer = styled(ContentBlock)`
  ${pageShellStyles}

  ${breakpointSmall(css`
    background-color: ${getThemeColor('backgroundFaintGrey')};
  `)}
`;

const PageContainerStyledInner = ({ className, ...props }) => (
  <PageContainer innerContainerClassName={className} {...props} />
);

const NavigationContainer = styled(PageContainerStyledInner)`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;

  ${breakpointSmall(`
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    grid-column-gap: ${gridGap};
  `)}

  ${breakpointMedium(`
    grid-template-columns: 276px minmax(0, 1fr);
  `)}
`;

const ContentContainer = styled.div.attrs(testRefToDataTestReference)`
  flex: 1 0 auto;
  min-width: 0;
  box-sizing: border-box;
`;

const BottomNavigation = styled(Navigation)`
  ${breakpointSmall(`display: none;`)}
`;

const DashboardNavigation = connect(
  (state) => ({
    ...userSelector(state),
  }),
  {},
)(
  withState({
    name: 'DashboardNavigation',
    mapPropsToInitialState: () => ({
      topNavExpanded: false,
      bottomNavExpanded: false,
    }),
    componentDidMount: ({ user }) => {
      const isUserBoAllVerified_ = isUserBoAllVerified(user);
      if (!isUserBoAllVerified_) {
        history.push(boVedaFailedUrl());
      }
    },
    Component: ({
      children,
      state: { topNavExpanded, bottomNavExpanded },
      setState,
      user,
    }) => {
      const toggleTopNav = () => setState({ topNavExpanded: !topNavExpanded });
      const toggleBottomNav = () =>
        setState({ bottomNavExpanded: !bottomNavExpanded });
      const closeNav = () =>
        setState({
          topNavExpanded: false,
          bottomNavExpanded: false,
        });
      return (
        <NavigationContainer noGutter testRef="dashboard-navigation">
          <Spacing bottom={{ base: 'x-large', breakpointSmall: 'none' }}>
            <Navigation
              onExpand={toggleTopNav}
              expanded={topNavExpanded}
              onItemSelect={closeNav}
              user={user}
              testRef="top-nav-bar"
            />
          </Spacing>
          <ContentContainer testRef="dashboard-page-content">
            {children}
          </ContentContainer>
          {Configuration.SHOW_BOTTOM_NAVIGATION && (
            <Spacing top="x-large">
              <BottomNavigation
                onExpand={toggleBottomNav}
                expanded={bottomNavExpanded}
                onItemSelect={closeNav}
                user={user}
                testRef="bottom-nav-bar"
              />
            </Spacing>
          )}
        </NavigationContainer>
      );
    },
  }),
);

export default DashboardNavigation;
